import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    GroupTableCustomSortComparators,
    normalizeGroupTableFilterText,
    toGroupTableMetrics,
} from '@/components/group-tables/group-table-view-model-primitives';
import { sortGroupTableValuesByDirection } from '@/components/group-tables/use-group-table-view-model-core';
import { useGroupTableDomainViewModel } from '@/components/group-tables/use-group-table-domain-view-model';
import { SortDirection } from '@/enums/sort';
import {
    readStoredProductsBrandFilter,
    readStoredProductsBrandSearch,
    readStoredProductsNameFilter,
    readStoredProductsProductFilter,
    readStoredProductsSort,
    writeStoredProductsBrandFilter,
    writeStoredProductsBrandSearch,
    writeStoredProductsNameFilter,
    writeStoredProductsProductFilter,
    writeStoredProductsSort,
} from '@/context/reviews-page-transport.helpers';

export type ProductsTableData = Pick<
    ProductGroupEntity,
    'id' | 'name' | 'brandName' | 'productCount' | 'productNames' | 'productArticles' | 'photos'
> & {
    brandGroupId?: string;
    reviewCount: number;
    answerCount: number;
    answerPercent: number | null;
    aiAnalysisCount: number;
    averageRating: number | null;
    nps: number | null;
    ai: boolean;
    manage: boolean;
};

const PRODUCTS_TABLE_SORT_COMPARATORS: GroupTableCustomSortComparators<ProductsTableData> = {
    name: (leftProduct, rightProduct, currentDirection) =>
        sortGroupTableValuesByDirection(leftProduct.name, rightProduct.name, currentDirection),
    brandName: (leftProduct, rightProduct, currentDirection) =>
        sortGroupTableValuesByDirection(
            normalizeGroupTableFilterText(leftProduct.brandName),
            normalizeGroupTableFilterText(rightProduct.brandName),
            currentDirection,
        ),
    productCount: (leftProduct, rightProduct, currentDirection) =>
        sortGroupTableValuesByDirection(leftProduct.productCount, rightProduct.productCount, currentDirection),
    ai: (leftProduct, rightProduct, currentDirection) =>
        sortGroupTableValuesByDirection(leftProduct.ai ? 1 : 0, rightProduct.ai ? 1 : 0, currentDirection),
};

type UseProductsTableViewModelArgs = {
    clientId: string;
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
    productsPage: number;
    productsOnPage: number;
    setProductsPage: (page: number) => void;
    setProductsPageCount: (count: number) => void;
    setProductsTotalRows: (count: number) => void;
};

export const useProductsTableViewModel = ({
    clientId,
    products,
    brandGroups,
    productsPage,
    productsOnPage,
    setProductsPage,
    setProductsPageCount,
    setProductsTotalRows,
}: UseProductsTableViewModelArgs) => {
    const [productFilter, setProductFilter] = useState('');
    const [brandFilter, setBrandFilter] = useState<string | undefined>();
    const [brandSearch, setBrandSearch] = useState<string | undefined>();
    const hydratedClientIdRef = useRef<string | null>(null);
    const [isStorageHydrated, setIsStorageHydrated] = useState(false);

    const mapProductGroupToTableData = useCallback((product: ProductGroupEntity): ProductsTableData => {
        const metrics = toGroupTableMetrics(product);

        return {
            id: product.id,
            name: product.name,
            brandName: product.brandGroupName || product.brandName,
            brandGroupId: product.brandGroupId,
            productCount: product.productCount,
            productArticles: Array.from(
                new Set(
                    (product.productArticles || [])
                        .map((productSku) => productSku.trim())
                        .filter((productSku): productSku is string => !!productSku),
                ),
            ),
            productNames: Array.from(
                new Set(
                    (product.productNames || [])
                        .map((productName) => productName.trim())
                        .filter((productName): productName is string => !!productName),
                ),
            ),
            photos: product.photos || null,
            ...metrics,
            ai: metrics.aiAnalysisCount > 0,
            manage: true,
        };
    }, []);
    const normalizedProductFilter = normalizeGroupTableFilterText(productFilter);

    const {
        data: availableProducts,
        nameFilter,
        setNameFilter,
        sort,
        filteredAndSortedData: filteredAndSortedProducts,
        pagedData: pagedProducts,
        onSortChanged,
    } = useGroupTableDomainViewModel<ProductGroupEntity, ProductsTableData>({
        entities: products,
        mapEntityToData: mapProductGroupToTableData,
        page: productsPage,
        rowsOnPage: productsOnPage,
        setPage: setProductsPage,
        setPageCount: setProductsPageCount,
        setTotalRows: setProductsTotalRows,
        filterData: (product, normalizedNameFilter) => {
            const matchesName = normalizedNameFilter ? product.name.toLowerCase().includes(normalizedNameFilter) : true;
            const matchesProduct = normalizedProductFilter
                ? [product.name, ...(product.productNames || []), ...(product.productArticles || [])].some(
                      (productValue) => normalizeGroupTableFilterText(productValue).includes(normalizedProductFilter),
                  )
                : true;
            const matchesBrand = brandFilter ? product.brandGroupId === brandFilter : true;

            return matchesName && matchesProduct && matchesBrand;
        },
        sortComparators: PRODUCTS_TABLE_SORT_COMPARATORS,
    });

    useEffect(() => {
        if (hydratedClientIdRef.current === clientId) {
            return;
        }

        hydratedClientIdRef.current = clientId;
        setIsStorageHydrated(false);
        setNameFilter(readStoredProductsNameFilter(clientId));
        setProductFilter(readStoredProductsProductFilter(clientId));
        setBrandFilter(readStoredProductsBrandFilter(clientId));
        setBrandSearch(readStoredProductsBrandSearch(clientId));

        const storedSort = readStoredProductsSort(clientId);
        if (storedSort.sortKey && storedSort.sortOrder) {
            onSortChanged(storedSort.sortKey as keyof ProductsTableData, storedSort.sortOrder);
        } else if (sort.sortKey && sort.sortOrder) {
            onSortChanged(sort.sortKey as keyof ProductsTableData, SortDirection.NONE);
        }
        setIsStorageHydrated(true);
    }, [clientId, onSortChanged, setNameFilter, sort.sortKey, sort.sortOrder]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredProductsNameFilter(clientId, nameFilter);
    }, [clientId, isStorageHydrated, nameFilter]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredProductsProductFilter(clientId, productFilter);
    }, [clientId, isStorageHydrated, productFilter]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredProductsBrandFilter(clientId, brandFilter);
    }, [brandFilter, clientId, isStorageHydrated]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredProductsBrandSearch(clientId, brandSearch);
    }, [brandSearch, clientId, isStorageHydrated]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredProductsSort(clientId, sort);
    }, [clientId, isStorageHydrated, sort.sortKey, sort.sortOrder]);

    const preparedBrands = useMemo(() => {
        const uniqueBrands = [...brandGroups].sort((leftBrandGroup, rightBrandGroup) =>
            leftBrandGroup.name.localeCompare(rightBrandGroup.name, 'ru', { sensitivity: 'base', numeric: true }),
        );

        const normalizedSearch = normalizeGroupTableFilterText(brandSearch);
        const visibleBrands = normalizedSearch
            ? uniqueBrands.filter((currentBrandGroup) =>
                  currentBrandGroup.name.toLowerCase().includes(normalizedSearch),
              )
            : uniqueBrands;

        return visibleBrands.map((currentBrandGroup) => ({
            name: currentBrandGroup.name,
            value: currentBrandGroup.id,
        }));
    }, [brandGroups, brandSearch]);

    return {
        availableProducts,
        nameFilter,
        setNameFilter,
        productFilter,
        setProductFilter,
        brandFilter,
        setBrandFilter,
        brandSearch,
        setBrandSearch,
        sort,
        preparedBrands,
        filteredAndSortedProducts,
        pagedProducts,
        onSortChanged,
    };
};
